import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
// Api
import {
  ADD_TICKET_TIER,
  CREATE_TICKET_VENUE,
  RESCHEDULE_TICKET_EVENT,
  UPDATE_TICKET_EVENT_DETAILS,
} from 'api/ticketing/mutations';
// Types
import {
  AddTicketTier,
  AddTicketTierVariables,
} from 'api/ticketing/types/AddTicketTier';
import {
  CreateTicketVenue,
  CreateTicketVenueVariables,
} from 'api/ticketing/types/CreateTicketVenue';
import {
  RescheduleTicketEvent,
  RescheduleTicketEventVariables,
} from 'api/ticketing/types/RescheduleTicketEvent';
import {
  UpdateTicketEventDetails,
  UpdateTicketEventDetailsVariables,
} from 'api/ticketing/types/UpdateTicketEventDetails';
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
import {
  AddTicketTierInput,
  CreateTicketVenueInput,
  RescheduleTicketEventInput,
  UpdateTicketEventDetailsInput,
} from 'api/graphql-global-types';

export type TicketEventChanges = {
  /** Resolves to the saved event, or null when the server sent none back. */
  updateDetails: (
    input: UpdateTicketEventDetailsInput
  ) => Promise<TicketEventDetailsFields | null>;
  /** Resolves to the saved event, or null when the server sent none back. */
  reschedule: (
    input: RescheduleTicketEventInput
  ) => Promise<TicketEventDetailsFields | null>;
  addTier: (input: AddTicketTierInput) => Promise<void>;
  /** Resolves to the new venue's id. */
  createVenue: (input: CreateTicketVenueInput) => Promise<string | undefined>;
  busy: boolean;
};

/** The changes an admin makes to an existing event's details, schedule, tiers and venue. */
export const useTicketEventChanges = (): TicketEventChanges => {
  const [updateDetailsMutation, { loading: updating }] = useMutation<
    UpdateTicketEventDetails,
    UpdateTicketEventDetailsVariables
  >(UPDATE_TICKET_EVENT_DETAILS);
  const [rescheduleMutation, { loading: rescheduling }] = useMutation<
    RescheduleTicketEvent,
    RescheduleTicketEventVariables
  >(RESCHEDULE_TICKET_EVENT);
  const [addTierMutation, { loading: addingTier }] = useMutation<
    AddTicketTier,
    AddTicketTierVariables
  >(ADD_TICKET_TIER);
  const [createVenueMutation, { loading: creatingVenue }] = useMutation<
    CreateTicketVenue,
    CreateTicketVenueVariables
  >(CREATE_TICKET_VENUE);

  const updateDetails = useCallback(
    async (
      input: UpdateTicketEventDetailsInput
    ): Promise<TicketEventDetailsFields | null> => {
      const { data } = await updateDetailsMutation({ variables: { input } });

      return data?.updateTicketEventDetails || null;
    },
    [updateDetailsMutation]
  );

  const reschedule = useCallback(
    async (
      input: RescheduleTicketEventInput
    ): Promise<TicketEventDetailsFields | null> => {
      const { data } = await rescheduleMutation({ variables: { input } });

      return data?.rescheduleTicketEvent || null;
    },
    [rescheduleMutation]
  );

  const addTier = useCallback(
    async (input: AddTicketTierInput): Promise<void> => {
      await addTierMutation({ variables: { input } });
    },
    [addTierMutation]
  );

  const createVenue = useCallback(
    async (input: CreateTicketVenueInput): Promise<string | undefined> => {
      const { data } = await createVenueMutation({ variables: { input } });

      return data?.createTicketVenue.id;
    },
    [createVenueMutation]
  );

  return {
    updateDetails,
    reschedule,
    addTier,
    createVenue,
    busy: updating || rescheduling || addingTier || creatingVenue,
  };
};
